import React, { useState, useEffect, useContext } from "react";
import { Col, Container, Row, Table, Button, Card, Pagination, Form, Spinner } from "react-bootstrap";
import { useTable, usePagination, useFilters } from "react-table";
import { toast, ToastContainer } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getFailedAttemptsReport } from "../../services/Login";

const DefaultColumnFilter = ({
    column: { filterValue, preFilteredRows, setFilter },
}) => {
    const count = preFilteredRows.length;

    return (
        <input
            value={filterValue || ""}
            onChange={(e) => {
                setFilter(e.target.value || undefined);
            }}
            placeholder={`Buscar en ${count} registros...`}
            className="form-control form-control-sm"
        />
    );
};

const LoginLogs = () => {
    const [failedAttempts, setFailedAttempts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 50,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [localPageIndex, setLocalPageIndex] = useState(0);
    const [recordsLimit, setRecordsLimit] = useState(50);
    const recordsPerPage = 10;

    const getFailureReasonText = (reason) => {
        const reasonMap = {
            'email_not_found': 'Email no encontrado',
            'invalid_password': 'Contraseña incorrecta',
            'account_locked': 'Cuenta bloqueada',
            'account_disabled': 'Cuenta deshabilitada',
            'too_many_attempts': 'Demasiados intentos',
            'expired_token': 'Token expirado',
            'invalid_token': 'Token inválido'
        };
        return reasonMap[reason] || reason || 'Motivo desconocido';
    };

    const defaultColumn = React.useMemo(() => ({
        Filter: DefaultColumnFilter
    }), []);

    const columns = React.useMemo(() => [
        {
            Header: "Fecha y Hora",
            accessor: "created_at",
            disableFilters: true,
            Cell: ({ value }) => (value && String(value)) || '-'
        },
        {
            Header: "Email",
            accessor: "email",
            Filter: DefaultColumnFilter, // esta sí tiene filtro
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Motivo del Fallo",
            accessor: "reason",
            disableFilters: true,        // <— importante
            Cell: ({ value }) => {
                const text = getFailureReasonText(value);
                return <span className="badge bg-danger">{text}</span>;
            }
        }
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        state: { pageIndex, pageSize },
        table,
    } = useTable(
        {
            columns,
            data: failedAttempts,
            defaultColumn,
            initialState: { pageIndex: 0, pageSize: recordsPerPage },
            manualPagination: false,
        },
        useFilters,
        usePagination
    );

    const fetchFailedAttempts = async (limit = recordsLimit) => {
        setIsLoading(true);
        try {
            const response = await getFailedAttemptsReport(1, limit);
            if (response.success) {
                setFailedAttempts(Array.isArray(response.data) ? response.data : []);
                setPagination(response.pagination);
                gotoPage(0);
                setLocalPageIndex(0);
            } else {
                toast.error("Error al obtener los intentos fallidos", {
                    autoClose: 2500,
                    closeOnClick: true,
                    pauseOnHover: true,
                });
            }
        } catch (error) {
            console.error("Error al cargar los intentos fallidos:", error);
            toast.error("Hubo un error al cargar los intentos fallidos", {
                autoClose: 2500,
                closeOnClick: true,
                pauseOnHover: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const exportToExcel = async () => {
        if (failedAttempts.length === 0) {
            toast.warning("No hay datos para exportar", {
                autoClose: 2500,
                closeOnClick: true,
                pauseOnHover: true,
            });
            return;
        }

        try {
            // Crear workbook
            const workbook = XLSX.utils.book_new();

            // Preparar datos para Excel
            const excelData = failedAttempts.map((attempt, index) => ({
                'No.': index + 1,
                'Fecha y Hora': attempt.created_at || 'No disponible',
                'Email': attempt.email || 'No disponible',
                'Motivo del Fallo': getFailureReasonText(attempt.reason),
                'ID del Intento': attempt.id || 'No disponible',
                'Fecha de Actualización': attempt.updated_at || 'No disponible'
            }));

            // Crear hoja de trabajo
            const worksheet = XLSX.utils.json_to_sheet(excelData);
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Intentos Fallidos');

            // Nombre del archivo
            const fileName = `Reporte_Intentos_Fallidos_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Escribir y descargar el archivo
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(data, fileName);

            toast.success("Archivo exportado exitosamente", {
                autoClose: 2500,
                closeOnClick: true,
                pauseOnHover: true,
            });
        } catch (error) {
            console.error("Error al exportar:", error);
            toast.error("Error al exportar el archivo", {
                autoClose: 2500,
                closeOnClick: true,
                pauseOnHover: true,
            });
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setRecordsLimit(newLimit);
        fetchFailedAttempts(newLimit);
    };

    useEffect(() => {
        fetchFailedAttempts();
    }, []);

    const handlePageChange = (newPageIndex) => {
        gotoPage(newPageIndex);
        setLocalPageIndex(newPageIndex);
    };

    return (
        <React.Fragment>
            <Container fluid>
                <Row className="mb-3">
                    <Col>
                        <h2 className="card-subtitle text-muted">
                            Reporte de Intentos Fallidos de Inicio de Sesión
                        </h2>
                        <p className="text-muted">
                            Historial de intentos fallidos de acceso al sistema con detalles del motivo
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col xl={12}>
                        <Card>
                            {/* <Card.Header>
                                <Row className="align-items-center">
                                    <Col xs={12} md={3} className="mb-2">
                                        <Form.Label className="small">Registros a mostrar:</Form.Label>
                                        <Form.Select
                                            value={recordsLimit}
                                            onChange={handleLimitChange}
                                            disabled={isLoading}
                                            size="sm"
                                        >
                                            <option value={25}>25 registros</option>
                                            <option value={50}>50 registros</option>
                                            <option value={100}>100 registros</option>
                                            <option value={200}>200 registros</option>
                                            <option value={500}>500 registros</option>
                                        </Form.Select>
                                    </Col>
                                    <Col xs={12} md={3} className="mb-2">
                                        <Form.Label className="small">&nbsp;</Form.Label>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => fetchFailedAttempts()}
                                            disabled={isLoading}
                                            className="w-100"
                                            size="sm"
                                        >
                                            <RefreshCw size={16} className="me-1" />
                                            {isLoading ? 'Cargando...' : 'Actualizar'}
                                        </Button>
                                    </Col>
                                    <Col xs={12} md={6} className="mb-2">
                                        <Form.Label className="small">&nbsp;</Form.Label>
                                        <Button
                                            variant="success"
                                            onClick={exportToExcel}
                                            disabled={isLoading || failedAttempts.length === 0}
                                            className="w-100"
                                            size="sm"
                                        >
                                            <FileSpreadsheet size={16} className="me-1" />
                                            Exportar a Excel
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Header> */}
                            <Card.Body>
                                {isLoading ? (
                                    <div className="d-flex justify-content-center py-5">
                                        <Spinner animation="border" role="status"><span className="visually-hidden">Cargando…</span></Spinner>
                                    </div>
                                ) : (
                                    <>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="card-title">
                                                Total de registros: {failedAttempts.length}
                                                {pagination.totalRecords > failedAttempts.length &&
                                                    ` (Mostrando ${failedAttempts.length} de ${pagination.totalRecords})`
                                                }
                                            </h5>
                                        </div>

                                        <Table striped bordered hover responsive {...getTableProps()}>
                                            <thead>
                                                {headerGroups.map((headerGroup) => (
                                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                                        {headerGroup.headers.map((column) => (
                                                            <th {...column.getHeaderProps()}>
                                                                {column.render("Header")}
                                                                <div>
                                                                    {column.canFilter ? column.render("Filter") : null}
                                                                </div>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </thead>
                                            <tbody {...getTableBodyProps()}>
                                                {page.map((row) => {
                                                    prepareRow(row);
                                                    return (
                                                        <tr {...row.getRowProps()}>
                                                            {row.cells.map((cell) => (
                                                                <td {...cell.getCellProps()}>
                                                                    {cell.render("Cell")}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>

                                        {/* Paginación personalizada */}
                                        <Row className="align-items-center">
                                            <Col sm={12} md={6}>
                                                <span>
                                                    Página {pageIndex + 1} de {pageOptions.length} - {" "}
                                                    Mostrando {page.length} de {failedAttempts.length} registros
                                                </span>
                                            </Col>
                                            <Col sm={12} md={6}>
                                                <Pagination className="justify-content-end">
                                                    <Pagination.First
                                                        onClick={() => handlePageChange(0)}
                                                        disabled={!canPreviousPage}
                                                    />
                                                    <Pagination.Prev
                                                        onClick={() => previousPage()}
                                                        disabled={!canPreviousPage}
                                                    />

                                                    {/* Páginas numéricas */}
                                                    {pageOptions.map((pageNum) => (
                                                        <Pagination.Item
                                                            key={pageNum}
                                                            active={pageNum === pageIndex}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum + 1}
                                                        </Pagination.Item>
                                                    ))}

                                                    <Pagination.Next
                                                        onClick={() => nextPage()}
                                                        disabled={!canNextPage}
                                                    />
                                                    <Pagination.Last
                                                        onClick={() => handlePageChange(pageCount - 1)}
                                                        disabled={!canNextPage}
                                                    />
                                                </Pagination>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </React.Fragment>
    );
};

export default LoginLogs;